<template>
  <footer class="footer">
    <div class="footer-content">
      <!-- 主要内容区 -->
      <div class="footer-main">
        <!-- Logo 和简介 -->
        <div class="footer-brand">
          <div class="footer-logo">
            <img src="@/assets/logo.png" alt="UpGame Logo" class="logo-image" />
          </div>
          <p class="footer-description">
            {{ t('footer.description') }}
          </p>
          <div class="social-links">
            <a href="#" class="social-link" aria-label="Twitter / X">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417a9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
              </svg>
            </a>
          </div>
        </div>

        <!-- 快速链接 -->
        <div class="footer-links">
          <div class="link-group">
            <h4 class="link-title">{{ t('footer.games') }}</h4>
            <ul class="link-list">
              <li><a href="#/games/poker" class="footer-link">{{ t('games.poker.title') }}</a></li>
              <li><a href="#/games/sudoku" class="footer-link">{{ t('games.sudoku.title') }}</a></li>
              <li><a href="#/games/riddles" class="footer-link">{{ t('games.riddles.title') }}</a></li>
            </ul>
          </div>

          <div class="link-group">
            <h4 class="link-title">{{ t('footer.resources') }}</h4>
            <ul class="link-list">
              <li><a href="#/how-to-play" class="footer-link">{{ t('footer.howToPlay') }}</a></li>
              <li><a href="#/tips" class="footer-link">{{ t('footer.tips') }}</a></li>
              <li><a href="#/faq" class="footer-link">{{ t('footer.faq') }}</a></li>
              <li><a href="#/support" class="footer-link">{{ t('footer.support') }}</a></li>
            </ul>
          </div>

          <div class="link-group">
            <h4 class="link-title">{{ t('footer.company') }}</h4>
            <ul class="link-list">
              <li><a href="#/about" class="footer-link">{{ t('footer.about') }}</a></li>
              <li><a href="#/contact" class="footer-link">{{ t('footer.contact') }}</a></li>
              <li><a href="#/privacy" class="footer-link">{{ t('footer.privacy') }}</a></li>
              <li><a href="#/terms" class="footer-link">{{ t('footer.terms') }}</a></li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 底部版权区 -->
      <div class="footer-bottom">
        <div class="footer-copyright">
          <p>&copy; {{ currentYear }} UpGame. {{ t('footer.allRightsReserved') }}</p>
          <p class="footer-made-with">
            {{ t('footer.madeWith') }}
            <span class="heart">❤️</span>
            {{ t('footer.forLearning') }}
          </p>
        </div>

        <!-- 语言选择器（移动端） -->
        <div class="footer-language hide-desktop">
          <span class="language-label">{{ t('footer.language') }}:</span>
          <div class="language-buttons">
            <button
              v-for="lang in ['en', 'zh', 'fr']"
              :key="lang"
              :class="['lang-btn', { active: currentLang === lang }]"
              @click="setLanguage(lang)"
            >
              {{ getLanguageName(lang) }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from '@/utils/i18n'

const { t, setLanguage, currentLang } = useI18n()

const currentYear = computed(() => new Date().getFullYear())

const getLanguageName = (lang) => {
  const names = {
    en: 'English',
    zh: '中文',
    fr: 'Français'
  }
  return names[lang] || lang
}
</script>

<style scoped>
.footer {
  background-color: var(--color-text-primary);
  color: var(--color-bg-light);
  margin-top: auto;
}

.footer-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: var(--space-5xl) var(--space-base) var(--space-2xl);
}

/* 主要内容区 */
.footer-main {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4xl);
  margin: 0 0 var(--space-4xl) 0;
  padding: 0;
  align-items: flex-start;
}

@media (min-width: 768px) {
  .footer-main {
    grid-template-columns: 1fr 2fr;
    gap: var(--space-5xl);
    align-items: flex-start;
  }
}

/* 品牌区域 */
.footer-brand {
  max-width: 320px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0;
  padding: 0;
  margin: 0;
  align-self: start;
}

.footer-logo {
  margin: 0;
  padding: 0;
  line-height: 0;
  flex-shrink: 0;
  display: block;
  vertical-align: top;
}

.logo-image {
  width: 120px;
  height: 120px;
  filter: brightness(0) invert(1);
  display: block;
  margin: 0;
  padding: 0;
  vertical-align: top;
}

.footer-description {
  color: #D1D5DB;
  line-height: 1.4;
  margin: 0 0 0 0;
  padding: 0;
  font-size: var(--font-size-small);
  flex-shrink: 0;
}

/* 社交链接 */
.social-links {
  display: flex;
  gap: var(--space-base);
  margin-top: var(--space-lg);
}

.social-link {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md);
  color: #D1D5DB;
  transition: all var(--transition-base) var(--ease-in-out);
}

.social-link:hover {
  background-color: var(--color-primary-orange);
  color: white;
  transform: translateY(-2px);
}

.social-link svg {
  width: 20px;
  height: 20px;
}

/* 链接区域 */
.footer-links {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-2xl);
  padding: 0;
  margin: 0;
  align-self: start;
}

.link-group {
  min-width: 0;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.link-title {
  font-family: var(--font-heading);
  font-size: var(--font-size-h5);
  font-weight: var(--font-weight-semibold);
  color: var(--color-bg-light);
  display: block;
  margin: 0;
  margin-top: 0;
  padding: 0;
  margin-bottom: var(--space-base);
  line-height: 1.2;
}

.link-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.link-list li {
  margin-bottom: var(--space-sm);
}

.footer-link {
  color: #D1D5DB;
  text-decoration: none;
  font-size: var(--font-size-small);
  transition: color var(--transition-base) var(--ease-in-out);
}

.footer-link:hover {
  color: var(--color-primary-orange);
}

/* 底部版权区 */
.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: var(--space-xl);
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
}

@media (min-width: 768px) {
  .footer-bottom {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.footer-copyright {
  text-align: center;
}

@media (min-width: 768px) {
  .footer-copyright {
    text-align: left;
  }
}

.footer-copyright p {
  color: #9CA3AF;
  font-size: var(--font-size-small);
  margin-bottom: var(--space-xs);
}

.footer-made-with {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-xs);
}

@media (min-width: 768px) {
  .footer-made-with {
    justify-content: flex-start;
  }
}

.heart {
  color: var(--color-danger);
  animation: heartbeat 1.5s ease-in-out infinite;
}

/* 移动端语言选择器 */
.footer-language {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-sm);
}

.language-label {
  color: #9CA3AF;
  font-size: var(--font-size-small);
}

.language-buttons {
  display: flex;
  gap: var(--space-sm);
}

.lang-btn {
  padding: var(--space-xs) var(--space-md);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-sm);
  background: transparent;
  color: #D1D5DB;
  font-size: var(--font-size-tiny);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out);
}

.lang-btn:hover {
  border-color: var(--color-primary-orange);
  color: var(--color-primary-orange);
}

.lang-btn.active {
  background: var(--color-primary-orange);
  border-color: var(--color-primary-orange);
  color: white;
}

/* 响应式调整 */
@media (max-width: 640px) {
  .footer-content {
    padding: var(--space-3xl) var(--space-base) var(--space-xl);
  }

  .footer-main {
    gap: var(--space-2xl);
    margin-bottom: var(--space-2xl);
  }

  .footer-links {
    grid-template-columns: 1fr;
    gap: var(--space-xl);
  }

  .social-links {
    justify-content: center;
  }
}

/* Dark Mode Overrides - Fix footer text visibility */
html.dark-mode .footer {
  background-color: #1a1a1a;
  color: #f5f5f5;
}

html.dark-mode .footer-description {
  color: #b0b0b0;
}

html.dark-mode .link-title {
  color: #f5f5f5;
}

html.dark-mode .footer-link {
  color: #b0b0b0;
}

html.dark-mode .footer-copyright p {
  color: #808080;
}

html.dark-mode .language-label {
  color: #808080;
}

html.dark-mode .lang-btn {
  color: #b0b0b0;
  border-color: rgba(255, 255, 255, 0.1);
}
</style>